import type { ITitle } from "containers/Titles/types";
import { formatLocaleDateString } from "helpers/utils";
import usePaymentStateUpdate from "hooks/usePaymentStateUpdate";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchBeneficiar } from "store/beneficiaryStore";
import type { ITerm } from "store/managementStore";
import type { IBeneficiaryPayment } from "store/paymentSlice";
import { EPaymentResourceType, fetchResourcePayment } from "store/paymentSlice";
import type { AppDispatch, RootState } from "store/store";
import { useAppSelector } from "store/store";
import { deleteTerm } from "store/termStore";

import DeleteTermModal from "../DeleteTermModal";
import LastElementTable from "../LastElementTable";
import type { LastElementTableColumns } from "../LastElementTable/type";
import ManagementTableAuthorComponent from "../ManagementTableAuthorComponent";
import ShowDetails from "../ShowDetails";
import TextButton from "../TextButton";
import RenewTermModal from "./RenewTermModal";
import styles from "./style.module.scss";
import { getRenewalPeriods, isTermRenewable, TermExpirationStatusWrapper } from "./TableColumnsFormat";

interface ITitlesTerm {
    currentTitle: ITitle;
    currentTitleName?: string;
    currentTitleId?: number;
    data?: ITerm[];
    usesMultiPeriod: boolean;
    isLoading?: boolean;
}

const tHeadStyles = {
    maxWidth: 200,
    fontWeight: 600,
    fontSize: "12px",
    color: "#676767",
    width: "min-content",
    padding: "10px 20px",
    backgroundColor: "#F6F6F6",
    fontFamily: "'Poppins', sans-serif",
    whiteSpace: "nowrap",
    zIndex: "0",
};

/**
 * Terms linked to title (we see list of beneficiaries).
 */
const TitlesTerms = (props: ITitlesTerm) => {
    const { currentTitle, usesMultiPeriod, isLoading = false } = props;

    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const navigate = useNavigate();

    const { periodsDateRange } = useAppSelector((state) => state.period);
    const { updatePaymentPageState } = usePaymentStateUpdate(periodsDateRange);

    const [isOpen, setIsOpen] = useState(false);
    const [currentTerm, setCurrentTerm] = useState<ITerm>();
    const storedTerms = useSelector((state: RootState) => state.titles.termAssociatedToTitle);
    const terms = props.data ?? storedTerms;
    const [isDeleteTermModalOpen, setIsDeleteTermModalOpen] = useState(false);
    const [termIdToDelete, setTermIdToDelete] = useState<number>();
    const [advancePaymentId, setAdvancePaymentId] = useState<number | null>(null);
    const [showRenewTermModal, setShowRenewTermModal] = useState(false);
    const [resourcePayment, setResourcePayment] = useState<IBeneficiaryPayment[]>([]);

    // Needed to figure out the period duration per title.
    const { periods, activePeriod } = useAppSelector((state) => state.period);

    const toggleDrawer =
        (anchor: any, open: any): any =>
        (event: any) => {
            if (
                event &&
                event.type === "keydown" &&
                ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")
            ) {
                return;
            }
            setIsOpen(open);
        };

    // App rebuilds its router on store updates, re-reading this same history entry (same key) before the
    // replace below lands. Opening the term once per entry keeps fetchBeneficiar from looping the render.
    const openedFromLocationKeyRef = useRef<string>();

    useEffect(() => {
        const termIdFromState = location.state?.termId;
        if (!termIdFromState || !terms.length || openedFromLocationKeyRef.current === location.key) {
            return;
        }
        const currTerm = terms.find((term) => term.id === termIdFromState);

        if (currTerm) {
            openedFromLocationKeyRef.current = location.key;
            handleShowDetails(currTerm);
            setAdvancePaymentId(location.state.advancePaymentId ?? null);
            const { termId: _termId, advancePaymentId: _advancePaymentId, ...restState } = location.state;
            navigate(location.pathname, { replace: true, state: restState });
        }
    }, [location.key, location.pathname, location.state, navigate, terms]);

    const handleShowDetails = (term: ITerm) => {
        setCurrentTerm(term);
        if (term.id !== currentTerm?.id) {
            dispatch(fetchBeneficiar(term?.beneficiarId));
        }
        setIsOpen(true);
    };

    const handleDeleteTerm = async (termId: number) => {
        const response = await dispatch(deleteTerm(termId));

        if (response.meta.requestStatus === "fulfilled") {
            setIsDeleteTermModalOpen(false);
            setIsOpen(false);
            setTermIdToDelete(undefined);
            await updatePaymentPageState();
        }
    };

    const handleOpenDeleteModal = async (termId: number) => {
        await dispatch(
            fetchResourcePayment({
                relatedResourceId: termId,
                resourceType: EPaymentResourceType.BENEFICIARY,
            }),
        ).then((res) => {
            if (res.meta.requestStatus === "fulfilled") {
                setResourcePayment(res.payload);
            }
        });
        setIsDeleteTermModalOpen(true);
        setTermIdToDelete(termId);
    };

    const termToDelete = useMemo(() => {
        const term = terms.find((elm) => elm.id === termIdToDelete);
        if (term) {
            return {
                title: term.title,
                beneficiar: term.author,
                representative: term.representative ?? "-",
            };
        }
        return null;
    }, [termIdToDelete, terms]);

    const termsByBeneficiaryColumns: LastElementTableColumns[] = [
        {
            id: "author",
            label: t("Beneficiars.Beneficiar"),
            align: "left",
            format: (value, id, currentRow) => {
                return (
                    <ManagementTableAuthorComponent
                        author={value}
                        avatarUrl={currentRow.avatarUrl}
                        representative={currentRow.representative}
                        beneficiaryId={currentRow.beneficiarId}
                        createdDate={currentRow.creationDate}
                    />
                );
            },
        },
        {
            id: "reference",
            label: t("Terms.Reference"),
        },
        {
            id: "",
            label: t("Terms.ReportingType"),
            align: "center",
            hidden: !usesMultiPeriod,
            format: (value, id, currentRow) => {
                const titlePeriodDuration = periods.find((period) => period.periodTypeId === currentRow?.periodTypeId)
                    ?.periodDuration;
                return t(`Terms.ReportingType_${titlePeriodDuration}`, {
                    defaultValue: t("Terms.UnknownDuration"),
                });
            },
        },
        {
            id: "endDate",
            align: "left",
            label: t("Terms.EndDate"),
            format: (value, id, currentRow) => {
                if (!activePeriod) return;
                return (
                    <TermExpirationStatusWrapper term={currentRow} periods={periods}>
                        <span>
                            {formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } })}
                        </span>
                    </TermExpirationStatusWrapper>
                );
            },
        },
        {
            id: "",
            label: "",
            align: "right",
            format: (value, id, currentRow) => {
                if (!activePeriod) return;
                const isRenewable = isTermRenewable(periods, currentRow);

                return (
                    <TextButton
                        text={isRenewable ? t("Terms.Renew") : t("Beneficiars.ShowDetails")}
                        handleClick={(e) =>
                            isRenewable ? handleShowRenewTermModal(e, currentRow) : handleShowDetails(currentRow)
                        }
                        style={{ fontFamily: "Poppins, sans-serif", fontSize: "12px" }}
                        underlineOnHover
                    />
                );
            },
        },
    ];

    const handleShowRenewTermModal = (e, term: ITerm) => {
        e.stopPropagation();
        setCurrentTerm(term);
        setShowRenewTermModal(true);
    };

    const handleShowTerm = (_termId: number, _id2: any, _type: any, term: ITerm) => {
        handleShowDetails(term);
    };

    return (
        <div className={styles["title-beneficiaries-terms-table"]}>
            {isOpen && (
                <ShowDetails
                    currentTerm={currentTerm}
                    currentTitle={currentTitle}
                    isOpen={isOpen}
                    setOpen={setIsOpen}
                    toggleDrawer={toggleDrawer}
                    deleteTerm={handleOpenDeleteModal}
                    termId={currentTerm?.id}
                    from={"titles"}
                    advancePayment={advancePaymentId}
                />
            )}
            <DeleteTermModal
                open={isDeleteTermModalOpen}
                onClose={() => setIsDeleteTermModalOpen(false)}
                onDelete={() => handleDeleteTerm(termIdToDelete as number)}
                resourceData={resourcePayment}
                termData={termToDelete}
            />

            <LastElementTable
                tableHeadStyles={tHeadStyles}
                columns={termsByBeneficiaryColumns}
                data={terms}
                columnsNumber={termsByBeneficiaryColumns.length}
                isLoading={isLoading}
                onRowClick={handleShowTerm}
                allowInfiniteScroll
                bottomFooterContent={<></>}
                dataType="terms"
            />

            {currentTerm && (
                <RenewTermModal
                    open={showRenewTermModal}
                    onClose={() => setShowRenewTermModal(false)}
                    termId={currentTerm.id}
                    activePeriodStart={getRenewalPeriods(periods, currentTerm.periodTypeId).activePeriod?.periodStart}
                    relatedResourceId={currentTerm.workId ?? currentTerm.titleId ?? 0}
                    relatedResource={currentTerm.workId != null ? "work" : "title"}
                />
            )}
        </div>
    );
};

export default TitlesTerms;
